/// <amd-module name="Widgets/QueryResultsWidget/QueryResultsWidget" />
import * as React from "react";

import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import * as VssLocation from "VSS/Platform/Location";

import * as TfsWidgetContext from "Tfs/Widget/Context";

import { QueryType, WorkItemFieldReference, WorkItemLink, WorkItemQueryResult } from "Dashboards/Clients/Contracts/WorkItemTracking";
import { WorkItemTrackingClientName } from "Widgets/QueryResultsWidget/clients/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Widgets/QueryResultsWidget/clients/WorkItemTracking.Types";

import { WidgetSizeConverter } from "Dashboards/Grid/WidgetSizeConverter";
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadingStatus, WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import { WidgetPresentationMode } from "Dashboards/WidgetContracts/WidgetProps";

import { getSuggestedOpenWorkItemBehavior, WorkItemResultsGrid } from "WidgetCommon/WorkItemResultsGrid/WorkItemResultsGrid";

import { UnconfiguredMessage } from "Reporting/Visuals/VisualMessages/UnconfiguredMessage";

import * as Resources from "Widgets/QueryResultsWidget/Resources";

export interface QueryResultsWidgetState {
    availableColumns: WorkItemFieldReference[];
    workItemIds: number[];
    workItemsCount: number;
    workItemRelations: WorkItemLink[] | undefined;
    queryType: QueryType;
    parsedSettings: QueryResultsWidgetSettings;
}

interface QueryResultsWidgetSettings {
    lastArtifactName: string;
    query: {
        queryId: string;
        queryName: string;
    };
    selectedColumns: WorkItemFieldReference[];
}

export class QueryResultsWidget extends VssComponent<WidgetProps, QueryResultsWidgetState> {
    private parsedSettings: QueryResultsWidgetSettings;
    private projectId: string;
    private witRestClient: IWorkItemTrackingRestClient;
    private _isMounted: boolean;

    constructor(props: WidgetProps, context: IVssComponentContext) {
        super(props);
        this.parsedSettings = this.getParsedSettings();
        this.projectId = TfsWidgetContext.getProject(context.pageContext).id;
    }

    public async componentDidMount() {
        this._isMounted = true;
        this.witRestClient = this.context.pageContext.getRestClient<IWorkItemTrackingRestClient>(WorkItemTrackingClientName);
        await this.getWorkItemIdsAndSetState();
        WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
    }

    public async componentDidUpdate(prevProps: WidgetProps) {
        if (this.props.settings !== prevProps.settings) {
            this.parsedSettings = this.getParsedSettings();
            await this.getWorkItemIdsAndSetState();
        }
    }

    // Public for unit testing
    public async getWorkItemIdsAndSetState() {
        try {
            if (this.parsedSettings) {
                const queryResults: WorkItemQueryResult = await this.witRestClient.queryById(this.parsedSettings.query.queryId, this.projectId);
                const workItemIds = this.populateWorkItemIds(queryResults);
                const workItemsCount = workItemIds ? workItemIds.length : 0;

                if (this._isMounted) {
                    this.setState(
                        {
                            availableColumns: queryResults.columns,
                            workItemIds: workItemIds,
                            workItemRelations: queryResults.workItemRelations,
                            workItemsCount: workItemsCount,
                            queryType: queryResults.queryType,
                            parsedSettings: this.parsedSettings
                        },
                        this.props.onWidgetLoaded
                    );
                }
            } else {
                this.props.onWidgetLoaded();
            }
        } catch (error) {
            this.props.onWidgetLoaded({ outcome: LoadingStatus.failure, outcomeDescription: error });
        }
    }

    public populateWorkItemIds(queryResults: WorkItemQueryResult): number[] {
        let workItemIds: number[] = [];

        if (queryResults.workItems) {
            workItemIds = queryResults.workItems.map<number>(workItem => workItem.id);
        } else if (queryResults.workItemRelations) {
            const workItemRelations = queryResults.workItemRelations;
            // Root nodes do not have a source.
            const rootWorkItemNodes = workItemRelations && workItemRelations.filter(relation => !relation.source);
            workItemIds = rootWorkItemNodes.map(workItemNode => workItemNode.target.id);
        }

        return workItemIds;
    }

    public getTitle(): string {
        const { workItemsCount } = this.state;
        const workItemsCountString = workItemsCount ? `(${workItemsCount.toString()})` : "";

        return `${this.props.name} ${workItemsCountString}`;
    }

    public render(): JSX.Element | null {
        if (!this.props.settings) {
            return <UnconfiguredMessage visualTitle={this.props.name} size={this.props.size} onClick={this.props.onConfigure} />;
        } else if (!this.state || !this.state.workItemIds) {
            return null;
        } else {
            const { workItemIds, workItemRelations, queryType } = this.state;
            return (
                <div
                    className={"query-results-widget" + (this.props.size.height <= WidgetSizeConverter.CellHeight ? " short-grid-display-mode" : "")}
                >
                    <WorkItemResultsGrid
                        storageId={this.props.id}
                        title={this.getTitle()}
                        columns={this.getColumns()}
                        itemsSource={workItemIds}
                        workItemRelations={workItemRelations}
                        queryType={queryType}
                        projectId={this.projectId}
                        widgetPropsForTelemetry={this.props}
                        size={this.props.size}
                        noDataCardId={3}
                        openWorkItemBehavior={getSuggestedOpenWorkItemBehavior(this.props.avoidShowingModalUI)}
                        pageSize={100}
                    >
                        {this.renderQueryLink()}
                    </WorkItemResultsGrid>
                </div>
            );
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    private getParsedSettings(): QueryResultsWidgetSettings {
        return this.props.settings ? JSON.parse(this.props.settings) : undefined;
    }

    private getQueryLink(): string {
        const queryResultsRouteId = "ms.vss-work-web.query-route";
        const queryResultsViewId = "query";
        return VssLocation.routeUrl(this.context.pageContext, queryResultsRouteId, {
            project: this.projectId,
            view: queryResultsViewId,
            id: this.state.parsedSettings ? this.state.parsedSettings.query.queryId : (undefined as any)
        });
    }

    private getColumns(): WorkItemFieldReference[] {
        let columns: WorkItemFieldReference[];
        if (this.props.presentationMode === WidgetPresentationMode.lightboxView) {
            // All columns are shown in lightbox mode
            columns = this.state.availableColumns;
        } else {
            columns = this.state.parsedSettings.selectedColumns;
        }

        return columns;
    }

    private renderQueryLink(): JSX.Element {
        return (
            <FPSLink className={"view-query-link"} href={this.getQueryLink()} target={this.props.openLinksInNewTab ? "_blank" : undefined}>
                {Resources.ViewQueryLinkText}
            </FPSLink>
        );
    }
}

VssComponent.register("queryResultsWidget", QueryResultsWidget);
